import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Sparkles, Zap, Crown, Check, Loader2, ExternalLink, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";

const currencySymbol = (c) => (c === "eur" ? "€" : c?.toUpperCase() + " ");
const fmt = (n, c) => `${n.toFixed(2)} ${currencySymbol(c)}`;

const FEATURES_FREE = [
  "Hasta 2 Espacios",
  "Hasta 5 miembros por Espacio",
  "Vistas Kanban, Lista, Calendario y Gantt",
  "Chats, Documentos, Gastos y Encuestas",
  "15 mensajes AI Space Brain al mes",
  "Plantillas predefinidas",
];

const FEATURES_PREMIUM = [
  "Espacios y miembros ilimitados",
  "Kanban avanzado (agrupaciones dinámicas)",
  "Guardar tus propias plantillas ilimitadas",
  "Export PDF (Mis Tareas, proyectos, calendario)",
  "Automatizaciones ilimitadas",
  "150 mensajes AI Space Brain al mes",
  "Invitaciones por email ilimitadas",
  "Soporte prioritario",
];

const PlanCard = ({ title, price, interval, description, features, cta, highlight, onCta, loading, current, savings }) => (
  <Card
    data-testid={`plan-card-${title.toLowerCase().replace(/\s/g, "-")}`}
    className={`relative p-6 flex flex-col gap-5 ${highlight ? "border-primary shadow-lg shadow-primary/10 ring-1 ring-primary/40" : ""}`}
  >
    {highlight && (
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
        Más popular
      </div>
    )}
    {savings && (
      <div className="absolute -top-3 right-4 bg-emerald-500 text-white text-xs font-semibold px-2 py-1 rounded-full">
        {savings}
      </div>
    )}
    <div>
      <h3 className="font-heading font-bold text-xl">{title}</h3>
      <p className="text-xs text-muted-foreground mt-1">{description}</p>
    </div>
    <div className="flex items-baseline gap-1">
      <span className="font-heading text-4xl font-bold">{price}</span>
      {interval && <span className="text-sm text-muted-foreground">/{interval}</span>}
    </div>
    {interval && <p className="text-[11px] text-muted-foreground -mt-3">IVA incluido · sin sorpresas al pagar</p>}
    <ul className="space-y-2 text-sm flex-1">
      {features.map((f) => (
        <li key={f} className="flex items-start gap-2">
          <Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
          <span>{f}</span>
        </li>
      ))}
    </ul>
    {current ? (
      <Button variant="outline" disabled className="w-full" data-testid={`plan-current-${title.toLowerCase().replace(/\s/g, "-")}`}>
        Tu plan actual
      </Button>
    ) : (
      <Button
        data-testid={`plan-cta-${title.toLowerCase().replace(/\s/g, "-")}`}
        onClick={onCta}
        disabled={loading}
        className="w-full"
        variant={highlight ? "default" : "outline"}
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : cta}
      </Button>
    )}
  </Card>
);

export default function SubscriptionPage() {
  const [loadingKey, setLoadingKey] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  const { data: sub, refetch: refetchSub } = useQuery({
    queryKey: ["subscription-me"],
    queryFn: async () => (await api.get("/subscription/me")).data,
    refetchInterval: 30000,
  });
  const { data: prices } = useQuery({
    queryKey: ["subscription-prices"],
    queryFn: async () => (await api.get("/subscription/prices")).data,
  });

  const priceMap = Object.fromEntries((prices?.prices || []).map((p) => [p.lookup_key, p]));
  const monthly = priceMap["nuxxum_premium_monthly"];
  const yearly = priceMap["nuxxum_premium_yearly"];
  const boosters = (prices?.prices || []).filter((p) => p.tokens).sort((a, b) => a.tokens - b.tokens);
  const yearlyDiscount = monthly && yearly
    ? Math.round((1 - yearly.amount / (monthly.amount * 12)) * 100)
    : null;

  const checkout = async (lookupKey) => {
    setLoadingKey(lookupKey);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e));
      setLoadingKey(null);
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/subscription/portal", {
        return_url: window.location.origin + "/subscription",
      });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(apiError(e));
      setPortalLoading(false);
    }
  };

  const isPremium = sub?.plan === "premium";
  const monthlyPct = sub?.ai_monthly_limit
    ? Math.min(100, Math.round((sub.ai_monthly_used / sub.ai_monthly_limit) * 100))
    : 0;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-12 space-y-8" data-testid="subscription-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
          <Crown className="h-7 w-7 text-primary" /> Suscripción y facturación
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Elige el plan que mejor se adapta a ti. Puedes cambiar o cancelar cuando quieras.
        </p>
      </div>

      {/* Estado actual */}
      <Card className="p-6 space-y-4" data-testid="subscription-current-status">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            {isPremium ? <Crown className="h-6 w-6 text-primary" /> : <Sparkles className="h-6 w-6 text-muted-foreground" />}
            <div>
              <p className="font-heading font-semibold text-lg">
                {isPremium ? "Plan Premium" : "Plan Base"}
              </p>
              <p className="text-xs text-muted-foreground">
                {sub?.subscription_status === "trialing" && sub?.trial_ends_at
                  ? `Trial hasta el ${new Date(sub.trial_ends_at).toLocaleDateString()}`
                  : sub?.current_period_end
                  ? `Renueva el ${new Date(sub.current_period_end).toLocaleDateString()}`
                  : "Sin suscripción activa"}
                {sub?.cancel_at_period_end && " (cancelación programada)"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => refetchSub()} data-testid="subscription-refresh-btn">
              <RefreshCw className="h-3.5 w-3.5 mr-1" /> Actualizar
            </Button>
            {isPremium && (
              <Button variant="outline" size="sm" onClick={openPortal} disabled={portalLoading} data-testid="subscription-portal-btn">
                {portalLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5 mr-1" />}
                Gestionar facturación
              </Button>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="font-medium">Mensajes AI este mes</span>
            <span className="text-muted-foreground">
              {sub?.ai_monthly_used || 0} / {sub?.ai_monthly_limit || 0}
              {sub?.ai_booster_credits > 0 && ` (+${sub.ai_booster_credits} booster)`}
            </span>
          </div>
          <Progress value={monthlyPct} className="h-2" data-testid="ai-usage-progress" />
          {sub?.ai_booster_tokens > 0 && (
            <div className="flex justify-between text-sm" data-testid="booster-tokens-balance">
              <span className="font-medium flex items-center gap-1"><Zap className="h-3.5 w-3.5 text-primary" /> Tokens Booster</span>
              <span className="text-muted-foreground">
                {sub.ai_booster_tokens.toLocaleString("es-ES")} tokens (~{sub.ai_booster_msgs_est} mensajes)
              </span>
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Se reinicia el día 1 de cada mes. Los tokens de los Booster Packs no caducan y se consumen solo al agotar tu cuota mensual.
          </p>
        </div>
      </Card>

      {/* Planes */}
      <div className="grid md:grid-cols-2 gap-6">
        <PlanCard
          title="Base"
          price="Gratis"
          description="Para empezar sin coste. Todo lo esencial para organizarte."
          features={FEATURES_FREE}
          cta="Continuar en Base"
          current={!isPremium}
        />
        {monthly && yearly && (
          <div className="space-y-3">
            <PlanCard
              title="Premium Mensual"
              price={fmt(monthly.amount, monthly.currency)}
              interval="mes"
              description={`Prueba gratis ${prices?.trial_days || 14} días. Sin compromiso.`}
              features={FEATURES_PREMIUM}
              cta={isPremium ? "Cambiar a mensual" : `Empezar prueba de ${prices?.trial_days || 14} días`}
              highlight
              onCta={() => checkout("nuxxum_premium_monthly")}
              loading={loadingKey === "nuxxum_premium_monthly"}
              current={isPremium && sub?.subscription_status === "active" && !sub?.cancel_at_period_end && new Date(sub?.current_period_end) - new Date() < 40 * 24 * 3600 * 1000}
            />
            <PlanCard
              title="Premium Anual"
              price={fmt(yearly.amount, yearly.currency)}
              interval="año"
              description={`Ahorra 2 meses (~${fmt(monthly.amount * 12 - yearly.amount, yearly.currency)}) pagando por adelantado.`}
              features={[`Equivale a ${fmt(yearly.amount / 12, yearly.currency)}/mes`, ...FEATURES_PREMIUM]}
              cta={isPremium ? "Cambiar a anual" : `Empezar prueba de ${prices?.trial_days || 14} días`}
              savings={yearlyDiscount ? `-${yearlyDiscount}%` : null}
              onCta={() => checkout("nuxxum_premium_yearly")}
              loading={loadingKey === "nuxxum_premium_yearly"}
            />
          </div>
        )}
      </div>

      {/* Boosters por tokens */}
      {boosters.length > 0 && (
        <div className="space-y-4">
          <div>
            <h2 className="font-heading font-bold text-xl flex items-center gap-2">
              <Zap className="h-5 w-5 text-primary" /> AI Booster Packs
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Tokens extra para el Space Brain cuando agotes tu cuota mensual. Pago único, <strong>nunca caducan</strong>. Un mensaje consume ~4.000 tokens de media (según el tamaño del espacio y la respuesta).
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {boosters.map((b, i) => {
              const size = ["S", "M", "L"][i] || "";
              return (
                <Card key={b.lookup_key} data-testid={`booster-card-${b.lookup_key}`}
                  className={`p-5 flex flex-col gap-3 ${i === 1 ? "ring-1 ring-primary/40 border-primary" : ""}`}>
                  {i === 1 && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-[11px] font-semibold px-2.5 py-0.5 rounded-full">
                      Mejor relación
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="h-9 w-9 rounded-xl bg-gradient-to-br from-primary to-indigo-600 flex items-center justify-center">
                      <Zap className="h-4 w-4 text-white" />
                    </span>
                    <h3 className="font-heading font-bold">Booster {size}</h3>
                  </div>
                  <div>
                    <p className="font-heading text-2xl font-bold">{(b.tokens / 1000).toLocaleString("es-ES")}K <span className="text-sm font-normal text-muted-foreground">tokens</span></p>
                    <p className="text-xs text-muted-foreground">≈ {b.msgs_est} mensajes AI</p>
                  </div>
                  <div className="flex items-baseline justify-between mt-auto">
                    <span className="font-heading text-xl font-bold">{fmt(b.amount, b.currency)}</span>
                    <span className="text-[11px] text-muted-foreground">{((b.amount / b.tokens) * 1000000).toFixed(2)} €/M</span>
                  </div>
                  <Button
                    data-testid={`booster-buy-${b.lookup_key}`}
                    onClick={() => checkout(b.lookup_key)}
                    disabled={loadingKey === b.lookup_key}
                    variant={i === 1 ? "default" : "outline"}
                    size="sm"
                  >
                    {loadingKey === b.lookup_key ? <Loader2 className="h-4 w-4 animate-spin" /> : "Comprar"}
                  </Button>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      <Card className="p-4 bg-secondary/50 border-none">
        <p className="text-xs text-muted-foreground text-center">
          Los pagos se procesan de forma segura por Stripe. <strong>Todos los precios incluyen IVA</strong>: el importe mostrado es el importe final que pagas. El IVA (21% en España) es un impuesto que recauda el gobierno de tu país, no Nuxxum, y se desglosa en tu factura de Stripe. Puedes cancelar tu suscripción cuando quieras desde &quot;Gestionar facturación&quot;.
        </p>
      </Card>
    </div>
  );
}
